import { ImageField, LinkField, StringField, THeadingTag, TIconField, TIconFromRenderingParams } from '../../../types/types';
import { TColHeadings, TGroupItemFields, TGroupItems, TRowFields, TRowItems, TTableFields, TTableProps } from '../types/index.types';
import { counterArray } from '../../../utils/counterArray.utils';
import { ComponentParams } from '@sitecore-jss/sitecore-jss-react';
import { BACKGROUND_COLOR_MAP, BACKGROUND_COLOR_CLASS_MAP } from '../../../constants/background-color.constants';
import { getFollowingHTag } from '../../../utils/seo.utils';
import { windowService } from '../../../utils/window.utils';
import { APIPrefix } from '../../../constants/api.constants';
export class TableRowModel {
  private readonly fields: TRowFields | undefined;

  constructor(props: TRowItems) {
    this.fields = props?.fields;
  }

  getCellData(number: number): StringField {
    return this.fields?.[`cell${number}`];
  }

  getIconCellData(number): TIconField {
    return this.fields?.[`icon_cell${number}`];
  }

  get groupName(): StringField | undefined {
    return this.fields?.groupName;
  }

  get icon(): ImageField | undefined {
    return this.fields?.icon;
  }

  get tooltipTitle(): StringField | undefined {
    return this.fields?.tooltipTitle;
  }

  get tooltip(): StringField | undefined {
    return this.fields?.tooltip;
  }

  get title(): StringField | undefined {
    return this.fields?.title;
  }
}

export class TableGroupModel {
  private readonly fields: TGroupItemFields | undefined;

  constructor(props: TGroupItems) {
    this.fields = props?.fields;
  }

  get title(): StringField | undefined {
    return this.fields?.title;
  }

  get items(): TableRowModel[] {
    return this.fields?.items?.map((item) => new TableRowModel(item)) || [];
  }
}

export class TableModel {
  private readonly fields: TTableFields | undefined;
  private readonly params: ComponentParams | undefined;

  constructor(props: TTableProps) {
    this.fields = props?.fields;
    this.params = props?.params;
  }

  get backgroundColor(): string | undefined {
    return BACKGROUND_COLOR_MAP.get(this.params?.backgroundColor || '');
  }

  get backgroundColorClass(): string | undefined {
    return BACKGROUND_COLOR_CLASS_MAP.get(this.params?.backgroundColor || '') || '';
  }

  get colHeadings(): TColHeadings[] {
    const totalColumns = Number(this.fields?.nOfVisibleColumn?.value);
    if (totalColumns) {
      return counterArray(totalColumns).map((num: number) => {
        return {
          text: this.getColumnTextData(num),
          image: this.getColumnImageData(num),
        };
      });
    }
    return [];
  }

  getColumnTextData(number: string | number): StringField {
    return this.fields?.[`column${number}Text`];
  }

  getColumnImageData(number: string | number): ImageField {
    return this.fields?.[`column${number}Image`];
  }

  get items(): Array<TableGroupModel | TableRowModel> {
    const items = this.fields?.items?.map((item) => {
      if ((item.fields as TGroupItemFields)?.templateId === '{467E4AEE-FF09-45E2-81F4-EE28518E8559}') {
        return new TableGroupModel(item);
      } else {
        return new TableRowModel(item);
      }
    });
    return items || [];
  }

  get groupItemsIndexes(): number[] {
    const newExpandedRows: number[] = [];
    this.items.forEach((item, index) => {
      if (item instanceof TableGroupModel) {
        newExpandedRows.push(index);
      }
    });
    return newExpandedRows;
  }

  get nOfVisibleColumn(): number {
    return Number(this.fields?.nOfVisibleColumn?.value);
  }

  get disableExpand(): boolean {
    return this.fields?.disableExpand?.value || false;
  }

  get disableSearch(): boolean {
    return this.fields?.tableSettings?.hideSearch === '1';
  }

  get disablePrint(): boolean {
    return this.fields?.tableSettings?.hidePrint === '1';
  }

  get disableExcel(): boolean {
    return this.fields?.tableSettings?.showDownloadExcel === '1';
  }

  get hideHeadingSection(): boolean {
    return this.fields?.tableSettings?.hideHeadingSectionOfTable === '1';
  }

  get hideRowsHeader(): boolean {
    return this.fields?.tableSettings?.hideFirstColumn === '1';
  }

  get hideImageOnHeadingRow(): boolean {
    return this.fields?.tableSettings?.hideImageOnHeadingRow === '1';
  }

  get hideAccordionTabIcon(): boolean {
    return this.fields?.tableSettings?.hideAccordionTabPlusMinusIcon === '1';
  }

  get hideColumnHeader(): boolean {
    return this.fields?.tableSettings?.hideHeadingRow === '1';
  }

  get showIconsOnCells(): boolean {
    return this.fields?.tableSettings?.showIconsOnCells === '1';
  }

  get stickyHeadingRow(): boolean {
    return this.fields?.stickyHeadingRow?.value || false;
  }

  get stickyHeadingColumn(): boolean {
    return this.fields?.stickyHeadingColumn?.value || false;
  }

  get heading(): StringField | undefined {
    return this.fields?.heading;
  }

  get link(): LinkField | undefined {
    return this.fields?.link;
  }

  get eyebrow(): StringField | undefined {
    return this.fields?.eyebrow;
  }

  get isUnreleased(): boolean {
    return !!this.fields?.isComponentUnreleased;
  }

  get headingTag(): THeadingTag {
    return (this.params?.headingTag?.toLowerCase() as THeadingTag) || 'h3';
  }

  get groupHeadingTag(): THeadingTag {
    return getFollowingHTag(this.headingTag);
  }

  rowTitlePlainText(richText: string): string {
    if (!windowService()?.DOMParser) return '';
    const parser = new DOMParser();
    const parseText = parser.parseFromString(richText, 'text/html').documentElement.textContent || '';
    return parseText;
  }

  get iconsList(): Array<TIconFromRenderingParams> {
    return this.fields?.iconsList || [];
  }
  

  get convertToExcelLink(): string {
    const specificationUrlParametersArray = [
      this.fields?.tableSettings?.exportDataId,
      this.fields?.tableSettings?.excelSettings || '-',
      this.fields?.tableSettings?.showIconsOnCells,
    ];

    const specificationUrlParameters = specificationUrlParametersArray.join('/');
    return `/${APIPrefix}/download/table/${specificationUrlParameters}`;
  }
}
